import React from 'react';
import classnames from 'classnames';
import {Form} from 'formik';
import dayjs from 'dayjs';
import FormTitle from '../FormTitle';
import FieldInput from 'common/components/FieldInput';
import FieldSelect from 'common/components/FieldSelect';
import UploadingFilePopUp from 'common/components/UploadingFilePopUp/UploadingFilePopUp';
import ButtonsSubmitCancel from '../ButtonsSubmitCancel';
import iconOpenInBrowser from 'common/assets/open_in_browser-24px.svg';
import ModalSharedFilesList from '../../../../common/components/ModalSharedFilesList';
import FieldTextareaAutosize from '../../../../common/components/FieldTextareaAutosize';
import FieldCheckbox from '../../../../common/components/FieldCheckbox';
import ModalTabuYashirAreYouSure from '../ModalTabuYashirAreYouSure/ModalTabuYashirAreYouSure-redux';
import ButtonTextSuggestions from '../../../../common/components/ButtonTextSuggestions';
import FieldRadiobutton from '../../../../common/components/FieldRadiobutton';
import FieldDatepicker from '../../../../common/components/FieldDatepicker';
import MultipleTooltip from '../../../../common/components/MultipleTooltip';
import FieldPasteFile from '../../../../common/components/FieldPasteFile';
import MarkFileUploaded from '../../../../common/components/MarkFileUploaded';
import FieldPasteImage from "../../../../common/components/FieldPasteImage";
import ListUploadedFilesViewDelete from "../ListUploadedFilesViewDelete";
import ViewFile from '../../../../common/components/ViewFile';
import {
  propertyRightsDetails,
  rightsPermitCompanyMeshakenet,
  rightsPermitOther,
  rightsPermitRami,
  notesText
} from '../../constants/textSuggestions';
import {
  limitFloatValueLength,
  limitIntegerValueLength,
  formatStringToNumber
} from 'common/utils/form/limitValueLength';
import {divideThousandsIntegerValue, allowFloatWithDecimals} from '../../../../common/utils/formatting';
import styles from './styles.module.scss';

const manualRetrievalLink = 'https://ecom.gov.il/voucherspa/input/440';

const Form_1View = ({
                      shumaID,
                      stepID,
                      values,
                      setFieldValue,
                      fileUploaded,
                      onNesachFileSelected,
                      onFileSelected,
                      fileTypeIds,
                      options,
                      cityChangeProps,
                      showUploadingFilePopUp,
                      progress,
                      prevPagePath,
                      nextPagePath,
                      fileSelectOptions: {
                        nesachTabu,
                        landShituf,
                        landHahira,
                        landHafkaa,
                        landHazmada,
                        landZikatHanaa,
                        landOther,
                        tama,
                        purchase,
                        rent,
                        report
                      },
                      propertyTypes: {
                        isLot
                      },
                      fileList,
                      filesListData
                    }) => {
  const {gush, helka, ttHelka, attached} = values;
  const selectedAttached = attached?.value || '';
  const propertyLegalDefinitionTooltip = `
  (1) אם הנכס רשום כחלקת משנה בפנקס הבתים המשותפים, יש לציין הגדרת הנכס כ"חלקת משנה 1 בחלקה 22 בגוש 3333, מכח רישום בפנקס הבתים המשותפים".
(2) אם הנכס מהווה חלק מסויים מחלקת רישום  -  יש לציין הגדרת הנכס "מחצית מזרחית של חלקה 11 בגוש 2222 , מכח הסכם שיתוף רשום/לא רשום וכו'". 
           (3) המצב התכנוני והקנייניאם הנכס מהווה מגרש (או חלק מסויים ממנו), יש לציין הגדרת הנכס "(מחצית צפונית של) מגרש 4 לפי תב"ע אאא, או בכל תשריט אחר שיש לציינו ולצרפו; וכיוצ"ב.
  `;

  return (
    <div className={styles.form_1}>
      <FormTitle title={'פרטי הנכס'}/>
      <Form>
        <div className={classnames(styles.threeQuartersField, styles.flexBetween, styles.marginBottom25)}>
          <FieldInput
            fieldId="gush"
            onChange={limitIntegerValueLength(5)}
            value={values.gush}
            mandatoryMark={true}
            label={'גוש'}
            containerCustomStyles={styles.quarterField}
          />
          <FieldInput
            fieldId="helka"
            onChange={limitIntegerValueLength(4)}
            value={values.helka}
            mandatoryMark={true}
            label={'חלקה'}
            containerCustomStyles={styles.quarterField}
          />
          <FieldInput
            fieldId="ttHelka"
            onChange={limitIntegerValueLength(3)}
            value={values.ttHelka}
            label="תת חלקה"
            containerCustomStyles={styles.quarterField}
          />
        </div>
        <div className={classnames(styles.marginBottom25, styles.flexBetween)}>
          <FieldInput
            fieldId="landLotNum"
            value={values.landLotNum}
            label='מספר מגרש תב"ע'
            containerCustomStyles={styles.halfField}
          />
          <FieldInput
            fieldId="landLotNumAccordingTaba"
            value={values.landLotNumAccordingTaba}
            label="לפי תב”ע"
            containerCustomStyles={styles.halfField}
          />
        </div>
        <FieldCheckbox
          fieldId={'noKadestritDivide'}
          label={'אין חלוקה קדסטרית'}
          value={values.noKadestritDivide}
          containerStyles={styles.marginBottom25}
        />
        {values.noKadestritDivide && (
          <FieldInput
            fieldId="noKadestritDivideText"
            value={values.noKadestritDivideText}
            mandatoryMark={true}
            label={'פירוט העדר חלוקה קדסטרית'}
            containerCustomStyles={styles.marginBottom25}
          />
        )}
        <FieldTextareaAutosize
          fieldId="registerDetailsNotes"
          value={values.registerDetailsNotes}
          label={'הערות לפרטי הרישום'}
          containerCustomStyles={styles.marginBottom25}
        />
        <div className={styles.unit}>
          <h3 className={classnames(styles.subHeader, styles.marginBottom25)}>קובץ נסח טאבו</h3>
          {!fileUploaded.nesachTabuFileUploaded && (
            <FieldCheckbox
              fieldId={'displayNoNesachText'}
              label={'לא להציג הערה על היעדר נסח'}
              value={values.displayNoNesachText}
              containerStyles={styles.marginBottom25}
            />
          )}
          <FieldCheckbox fieldId={'fileType1Switch'}/>
          <div className={styles.attachFileContainer}>
            {nesachTabu.type &&
              <>
                <ModalSharedFilesList file={nesachTabu} shumaID={shumaID} stepID={stepID}/>
                <div className={styles.divideLine}/>
              </>
            }
            <ModalTabuYashirAreYouSure
              data={{gush, helka, ttHelka, shumaID}}
              onApprove={onNesachFileSelected}
            />
            <div className={styles.divideLine}/>
            <a
              href={manualRetrievalLink}
              className={styles.manualRetrieval}
              target="_blank" rel="noreferrer"
            >
              <img
                src={iconOpenInBrowser} className={styles.iconOpenInBrowser} alt="i"
              />
              <span>אתר</span>
            </a>
            <div className={styles.attachFile}>
              {fileUploaded.nesachTabuFileUploaded && <MarkFileUploaded/>}
              {!values.fileType1Switch
                ? (
                  <FieldPasteFile
                    fieldId={'file'}
                    fileUploaded={fileUploaded.nesachTabuFileUploaded}
                    onFileSelected={onFileSelected(fileTypeIds.nesachTabu)()}
                    setFieldValue={setFieldValue}
                  />
                )
                : (
                  <FieldPasteImage
                    fieldId={'file'}
                    fileUploaded={fileUploaded.nesachTabuFileUploaded}
                    onFileSelected={onFileSelected(fileTypeIds.nesachTabu)()}
                    setFieldValue={setFieldValue}
                    formFields={'onlyFreeText'}
                  />
                )
              }
            </div>
          </div>
          <ListUploadedFilesViewDelete
            data={filesListData}
            fileTypeID={fileTypeIds.nesachTabu}
            shumaId={shumaID}
          />
          <div className={classnames(styles.flexBetween, styles.marginBottom25)}>
            <FieldSelect
              fieldId="city"
              value={values.city}
              placeholder="עיר"
              mandatoryMark={true}
              label={'עיר'}
              options={options.cities}
              isSearchable={true}
              isAsync={true}
              containerStyles={styles.halfField}
              {...cityChangeProps}
            />
            <FieldSelect
              fieldId="street"
              value={values.street}
              placeholder="רחוב"
              label={'רחוב'}
              options={options.streets}
              isSearchable={true}
              isAsync={true}
              defaultOptions={true}
              disabled={!values.city}
              containerStyles={styles.halfField}
            />
          </div>
          <div className={classnames(
            styles.flexBetween, styles.marginBottom25,
            {[styles.halfField]: isLot}
          )}>
            <FieldInput
              fieldId="houseNumber"
              value={values.houseNumber}
              label={'מספר בית / בניין'}
              containerCustomStyles={styles.quarterField}
            />
            {!isLot && (
              <FieldInput
                fieldId="apartmentNumber"
                value={values.apartmentNumber}
                label={'מספר דירה'}
                containerCustomStyles={styles.quarterField}
              />
            )}
            {!isLot && (
              <FieldInput
                fieldId="entrance"
                value={values.entrance}
                label="כניסה"
                containerCustomStyles={styles.quarterField}
              />
            )}
            <FieldInput
              fieldId="surveyArea"
              onChange={limitFloatValueLength(9)}
              value={values.surveyArea}
              label={(
                <div>
                  שטח חלקה נסקר
                  <span className={styles.topIndex}> (1)</span>
                </div>
              )}
              containerCustomStyles={styles.quarterField}
            />
          </div>
          <FieldTextareaAutosize
            fieldId="addressGeneralNotes"
            value={values.addressGeneralNotes}
            label={'הערות'}
            containerCustomStyles={styles.marginBottom25}
          />
        </div>
        <div className={classnames(styles.unit, styles.noBorder)}>
          <FormTitle
            title={'המצב המשפטי'}
            className={styles.unitBoldTitle}
          />
          <h3 className={classnames(styles.subHeader, styles.marginBottom25)}>
            זיהוי הנכס - לפי נסח רישום
          </h3>
          <div className={classnames(styles.flexBetween, styles.halfField, styles.marginBottom25)}>
            <FieldRadiobutton
              variant={'dot'}
              fieldId={'identifyMethodType'}
              value={'1'}
              label={'פנקס הבתים המשותפים'}
            />
            <FieldRadiobutton
              variant={'dot'}
              fieldId={'identifyMethodType'}
              value={'2'}
              label={'פנקס הזכויות'}
            />
          </div>
          {values.identifyMethodType === '1' && (
            <>
              <div className={classnames(styles.flexBetween, styles.marginBottom25)}>
                <FieldDatepicker
                  fieldId="rightsRegistryDate"
                  label={'תאריך'}
                  containerStyles={styles.halfField}
                />
                <FieldInput
                  fieldId="rightsRegistryDesc"
                  value={values.rightsRegistryDesc}
                  label={'תאור'}
                  containerCustomStyles={styles.halfField}
                />
              </div>
              <div className={classnames(styles.flexBetween, styles.marginBottom25)}>
                <FieldSelect
                  fieldId="attached"
                  mandatoryMark={true}
                  label={'הצמדות'}
                  options={options.attachedOptions}
                  containerStyles={styles.quarterField}
                />
                <FieldInput
                  fieldId="sharedParts"
                  value={values.sharedParts}
                  mandatoryMark={true}
                  label={'חלקים ברכוש המשותף'}
                  containerCustomStyles={styles.quarterField}
                />
                <FieldInput
                  fieldId="floor"
                  value={values.floor}
                  label={'קומה'}
                  containerCustomStyles={styles.halfField}
                />
              </div>
              {selectedAttached === 1 &&
                <FieldTextareaAutosize
                  fieldId="attachedText"
                  value={values.attachedText}
                  mandatoryMark={true}
                  label={'פירוט הצמדות'}
                  containerCustomStyles={styles.marginBottom25}
                />
              }
              <FieldTextareaAutosize
                fieldId="rightsOwnerName"
                value={values.rightsOwnerName}
                mandatoryMark={true}
                label={'שם בעל הזכויות'}
                containerCustomStyles={styles.marginBottom25}
              />
              <div className={classnames(styles.flexBetween, styles.marginBottom25)}>
                <FieldSelect
                  fieldId="rightsType"
                  placeholder="בחר אפשרות"
                  mandatoryMark={true}
                  label={'סוג זכויות'}
                  options={options.rightsTypeOptions}
                  containerStyles={styles.quarterField}
                />
                <FieldSelect
                  fieldId="rightsRegistryRegulations"
                  options={options.rightsRegistryRegulationsOptions}
                  label={'תקנון'}
                  containerStyles={styles.quarterField}
                />
                <FieldInput
                  fieldId="registeredArea"
                  onBeforeInput={(e) => allowFloatWithDecimals(e, 2)}
                  value={values.registeredArea}
                  label={(
                    <div>
                      <span>שטח חלקה רשום</span>
                      <span className={styles.topIndex}> (1)</span>
                    </div>
                  )}
                  containerCustomStyles={styles.halfField}
                />
              </div>
              <FieldTextareaAutosize
                fieldId="rightsRegistryMortgage"
                value={values.rightsRegistryMortgage}
                label={(
                  <div className={styles.flex}>
                    <span className={styles.marginLeftHalf}>משכנתה</span>
                    <MultipleTooltip
                      id={'rightsRegistryMortgageTooltip'}
                      label={'לטובת, הלווים, סכום'}
                    />
                  </div>
                )}
                containerCustomStyles={styles.marginBottom25}
              />
              <FieldTextareaAutosize
                fieldId="other"
                value={values.other}
                label={'זיהוי אחר'}
                containerCustomStyles={styles.marginBottom25}
              />
              <FieldCheckbox
                fieldId="warningNote"
                label={'הערות בנסח הרישום'}
                value={values.warningNote}
                containerStyles={styles.marginBottom25}
              />
              {values.warningNote &&
                <FieldTextareaAutosize
                  fieldId="warningNoteText"
                  value={values.warningNoteText}
                  mandatoryMark={true}
                  label={
                    <span>
                      <span>פירוט</span>
                      <ButtonTextSuggestions
                        fieldName={'warningNoteText'}
                        currentFieldValue={values.warningNoteText}
                        textSuggestionsArray={notesText}
                        setFieldValue={setFieldValue}
                      />
                    </span>
                  }
                  containerCustomStyles={styles.marginBottom25}
                />
              }
            </>
          )}
          {values.identifyMethodType === '2' && (
            <>
              <FieldDatepicker
                fieldId="joinHomeRegistryDate"
                label={'תאריך'}
                containerStyles={classnames(styles.halfField, styles.marginBottom25)}
              />
              <FieldTextareaAutosize
                fieldId="joinHomeRegistryRightsType"
                value={values.joinHomeRegistryRightsType}
                label={'סוג זכויות'}
                containerCustomStyles={styles.marginBottom25}
              />
              <FieldTextareaAutosize
                fieldId="joinHomeRegistryOwnerName"
                value={values.joinHomeRegistryOwnerName}
                label={'שם בעל הזכויות'}
                containerCustomStyles={styles.marginBottom25}
              />
              <FieldTextareaAutosize
                fieldId="joinHomeRegistryMortgage"
                value={values.joinHomeRegistryMortgage}
                label={(
                  <div className={styles.flex}>
                    <span className={styles.marginLeftHalf}>משכנתה</span>
                    <MultipleTooltip
                      id={'joinHomeRegistryMortgageTooltip'}
                      label={'לטובת, הלווים, סכום'}
                    />
                  </div>
                )}
                containerCustomStyles={styles.marginBottom25}
              />
              <FieldTextareaAutosize
                fieldId="joinHomeRegistryNotes"
                value={values.joinHomeRegistryNotes}
                label={
                  <div>
                    <span>הערות בנסח הרישום</span>
                    <ButtonTextSuggestions
                      fieldName={'joinHomeRegistryNotes'}
                      currentFieldValue={values.joinHomeRegistryNotes}
                      textSuggestionsArray={notesText}
                      setFieldValue={setFieldValue}
                    />
                  </div>
                }
                containerCustomStyles={styles.marginBottom25}
              />
            </>
          )}
          <div className={styles.landsContainer}>
            <div className={classnames(styles.flexBetween, styles.column_1)}>
              <FieldCheckbox
                fieldId="landShituf"
                label="הסכם שיתוף"
              />
              <FieldCheckbox
                fieldId="landHafkaa"
                label="הפקעה"
              />
              <FieldCheckbox
                fieldId="landZikatHanaa"
                label="זיקת הנאה"
              />
            </div>
            <div className={classnames(styles.flexBetween, styles.column_2)}>
              <FieldCheckbox
                fieldId="landHahira"
                label="חכירה"
              />
              <FieldCheckbox
                fieldId="landHazmada"
                label="הצמדה של הקרקע"
              />
              <FieldCheckbox
                fieldId="landOther"
                label="אחר"
              />
            </div>
          </div>
          <div className={styles.landsFields}>
            {values.landShituf &&
              <div>
                <div className={styles.details}>
                  <FieldTextareaAutosize
                    fieldId="landShitufText"
                    value={values.landShitufText}
                    label="פירוט הסכם שיתוף"
                  />
                </div>
                <h4 className={classnames(styles.buttonsSubheader, styles.marginBottom25)}>קובץ הסכם שיתוף</h4>
                <FieldCheckbox fieldId={'fileType3Switch'} />
                <div className={styles.attachFileContainer}>
                  {landShituf.type && (
                    <>
                      <ModalSharedFilesList file={landShituf} shumaID={shumaID} stepID={stepID}/>
                      <div className={styles.divideLine}/>
                    </>
                  )}
                  <div className={styles.attachFile}>
                    {fileUploaded.landShitufFileUploaded && <MarkFileUploaded/>}
                    {!values.fileType3Switch
                      ? (
                        <FieldPasteFile
                          fieldId={'landShitufFileUploaded'}
                          fileUploaded={fileUploaded.landShitufFileUploaded}
                          onFileSelected={onFileSelected(fileTypeIds.landShituf)()}
                          setFieldValue={setFieldValue}
                        />
                      )
                      : (
                        <FieldPasteImage
                          fieldId={'landShitufFileUploaded'}
                          fileUploaded={fileUploaded.landShitufFileUploaded}
                          onFileSelected={onFileSelected(fileTypeIds.landShituf)()}
                          setFieldValue={setFieldValue}
                          formFields={'onlyFreeText'}
                        />
                      )
                    }
                  </div>
                </div>
                <ListUploadedFilesViewDelete
                  data={filesListData}
                  fileTypeID={fileTypeIds.landShituf}
                  shumaId={shumaID}
                />
                <FieldInput
                  fieldId="landLotNumShituf"
                  value={values.landLotNumShituf}
                  label="מספר מגרש לפי הסכם שיתוף"
                  containerCustomStyles={classnames(styles.halfField, styles.marginBottom25)}
                />
              </div>
            }
            {values.landHahira &&
              <div>
                <FieldTextareaAutosize
                  fieldId="landHahiraText"
                  value={values.landHahiraText}
                  label="פירוט הסכם חכירה"
                  containerCustomStyles={styles.details}
                />
                <h4 className={classnames(styles.buttonsSubheader, styles.marginBottom25)}>קובץ הסכם חכירה</h4>
                <FieldCheckbox fieldId={'fileType4Switch'} />
                <div className={styles.attachFileContainer}>
                  {landHahira.type && (
                    <>
                      <ModalSharedFilesList file={landHahira} shumaID={shumaID} stepID={stepID}/>
                      <div className={styles.divideLine}/>
                    </>
                  )}
                  <div className={styles.attachFile}>
                    {fileUploaded.landHahiraFileUploaded && <MarkFileUploaded/>}
                    {!values.fileType4Switch
                      ? (
                        <FieldPasteFile
                          fieldId={'landHahiraFileUploaded'}
                          fileUploaded={fileUploaded.landHahiraFileUploaded}
                          onFileSelected={onFileSelected(fileTypeIds.landHahira)()}
                          setFieldValue={setFieldValue}
                        />
                      )
                      : (
                        <FieldPasteImage
                          fieldId={'landHahiraFileUploaded'}
                          fileUploaded={fileUploaded.landHahiraFileUploaded}
                          onFileSelected={onFileSelected(fileTypeIds.landHahira)()}
                          setFieldValue={setFieldValue}
                          formFields={'onlyFreeText'}
                        />
                      )
                    }
                  </div>
                </div>
                <ListUploadedFilesViewDelete
                  data={filesListData}
                  fileTypeID={fileTypeIds.landHahira}
                  shumaId={shumaID}
                />
              </div>
            }
            {values.landHafkaa &&
              <div>
                <FieldTextareaAutosize
                  fieldId="landHafkaaText"
                  value={values.landHafkaaText}
                  label="פירוט הפקעה"
                  containerCustomStyles={styles.details}
                />
                <h4 className={classnames(styles.buttonsSubheader, styles.marginBottom25)}>קובץ מסמכי הפקעה</h4>
                <FieldCheckbox fieldId={'fileType5Switch'} />
                <div className={styles.attachFileContainer}>
                  {landHafkaa.type && (
                    <>
                      <ModalSharedFilesList file={landHafkaa} shumaID={shumaID} stepID={stepID}/>
                      <div className={styles.divideLine}/>
                    </>
                  )}
                  <div className={styles.attachFile}>
                    {fileUploaded.landHafkaaFileUploaded && <MarkFileUploaded/>}
                    {!values.fileType5Switch
                      ? (
                        <FieldPasteFile
                          fieldId={'landHafkaaFileUploaded'}
                          fileUploaded={fileUploaded.landHafkaaFileUploaded}
                          onFileSelected={onFileSelected(fileTypeIds.landHafkaa)()}
                          setFieldValue={setFieldValue}
                        />
                      )
                      : (
                        <FieldPasteImage
                          fieldId={'landHafkaaFileUploaded'}
                          fileUploaded={fileUploaded.landHafkaaFileUploaded}
                          onFileSelected={onFileSelected(fileTypeIds.landHafkaa)()}
                          setFieldValue={setFieldValue}
                          formFields={'onlyFreeText'}
                        />
                      )
                    }
                  </div>
                </div>
                <ListUploadedFilesViewDelete
                  data={filesListData}
                  fileTypeID={fileTypeIds.landHafkaa}
                  shumaId={shumaID}
                />
              </div>
            }
            {values.landHazmada &&
              <div>
                <FieldTextareaAutosize
                  fieldId="landHazmadaText"
                  value={values.landHazmadaText}
                  label="פירוט הצמדה של הקרקע"
                  containerCustomStyles={styles.details}
                />
                <h4 className={classnames(styles.buttonsSubheader, styles.marginBottom25)}>קובץ מסמכי הצמדה של
                  הקרקע</h4>
                <FieldCheckbox fieldId={'fileType6Switch'} />
                <div className={styles.attachFileContainer}>
                  {landHazmada.type && (
                    <>
                      <ModalSharedFilesList file={landHazmada} shumaID={shumaID} stepID={stepID}/>
                      <div className={styles.divideLine}/>
                    </>
                  )}
                  <div className={styles.attachFile}>
                    {fileUploaded.landHazmadaFileUploaded && <MarkFileUploaded/>}
                    {!values.fileType6Switch
                      ? (
                        <FieldPasteFile
                          fieldId={'landHazmadaFileUploaded'}
                          fileUploaded={fileUploaded.landHazmadaFileUploaded}
                          onFileSelected={onFileSelected(fileTypeIds.landHazmada)()}
                          setFieldValue={setFieldValue}
                        />
                      )
                      : (
                        <FieldPasteImage
                          fieldId={'landHazmadaFileUploaded'}
                          fileUploaded={fileUploaded.landHazmadaFileUploaded}
                          onFileSelected={onFileSelected(fileTypeIds.landHazmada)()}
                          setFieldValue={setFieldValue}
                          formFields={'onlyFreeText'}
                        />
                      )
                    }
                  </div>
                </div>
                <ListUploadedFilesViewDelete
                  data={filesListData}
                  fileTypeID={fileTypeIds.landHazmada}
                  shumaId={shumaID}
                />
              </div>
            }
            {values.landZikatHanaa &&
              <div className={styles.marginBottom25}>
                <FieldTextareaAutosize
                  fieldId="landZikatHanaaText"
                  value={values.landZikatHanaaText}
                  mandatoryMark={true}
                  label={'פירוט זיקת הנאה'}
                  containerCustomStyles={styles.details}
                />
                <h4 className={classnames(styles.buttonsSubheader, styles.marginBottom25)}>קובץ מסמך זיקת הנאה</h4>
                <FieldCheckbox fieldId={'fileType7Switch'} />
                <div className={styles.attachFileContainer}>
                  {landZikatHanaa.type && (
                    <>
                      <ModalSharedFilesList file={landZikatHanaa} shumaID={shumaID} stepID={stepID}/>
                      <div className={styles.divideLine}/>
                    </>
                  )}
                  <div className={styles.attachFile}>
                    {fileUploaded.landZikatHanaaFileUploaded && <MarkFileUploaded/>}
                    {!values.fileType7Switch
                      ? (
                        <FieldPasteFile
                          fieldId={'landZikatHanaaFileUploaded'}
                          fileUploaded={fileUploaded.landZikatHanaaFileUploaded}
                          onFileSelected={onFileSelected(fileTypeIds.landZikatHanaa)()}
                          setFieldValue={setFieldValue}
                        />
                      )
                      : (
                        <FieldPasteImage
                          fieldId={'landZikatHanaaFileUploaded'}
                          fileUploaded={fileUploaded.landZikatHanaaFileUploaded}
                          onFileSelected={onFileSelected(fileTypeIds.landZikatHanaa)()}
                          setFieldValue={setFieldValue}
                          formFields={'onlyFreeText'}
                        />
                      )
                    }
                  </div>
                </div>
                <ListUploadedFilesViewDelete
                  data={filesListData}
                  fileTypeID={fileTypeIds.landZikatHanaa}
                  shumaId={shumaID}
                />
              </div>
            }
            {values.landOther &&
              <div className={styles.marginBottom25}>
                <FieldTextareaAutosize
                  fieldId="landOtherText"
                  value={values.landOtherText}
                  mandatoryMark={true}
                  label={'אחר'}
                  containerCustomStyles={styles.details}
                />
                <div className={classnames(styles.viewFileContainer, styles.marginBottom25)}>
                  <h4 className={styles.buttonsSubheader}>קובץ אחר</h4>
                  {fileUploaded.landOtherFileUploaded && (
                    <ViewFile
                      fileTypeID={fileTypeIds.landOther}
                      fileList={fileList}
                    />
                  )}
                </div>
                <div className={styles.attachFileContainer}>
                  {landOther.type &&
                    <>
                      <ModalSharedFilesList file={landOther} shumaID={shumaID} stepID={stepID}/>
                      <div className={styles.divideLine}/>
                    </>
                  }
                  <div className={styles.attachFile}>
                    <FieldPasteFile
                      fieldId={'landOtherFileUploaded'}
                      fileUploaded={fileUploaded.landOtherFileUploaded}
                      onFileSelected={onFileSelected(fileTypeIds.landOther)()}
                      setFieldValue={setFieldValue}
                    />
                    {fileUploaded.landOtherFileUploaded && <MarkFileUploaded/>}
                  </div>
                </div>
              </div>
            }
          </div>
          <FieldTextareaAutosize
            fieldId="rightsPermitCompanyMeshakenet"
            value={values.rightsPermitCompanyMeshakenet}
            label={
              <div>
                <span>אישור זכויות חברה משכנת</span>
                <ButtonTextSuggestions
                  fieldName={'rightsPermitCompanyMeshakenet'}
                  currentFieldValue={values.rightsPermitCompanyMeshakenet}
                  textSuggestionsArray={rightsPermitCompanyMeshakenet}
                  setFieldValue={setFieldValue}
                />
              </div>
            }
            containerCustomStyles={styles.marginBottom25}
          />
          <FieldTextareaAutosize
            fieldId="rightsPermitRami"
            value={values.rightsPermitRami}
            label={
              <div>
                <span>אישור זכויות רמ"י</span>
                <ButtonTextSuggestions
                  fieldName={'rightsPermitRami'}
                  currentFieldValue={values.rightsPermitRami}
                  textSuggestionsArray={rightsPermitRami}
                  setFieldValue={setFieldValue}
                />
              </div>
            }
            containerCustomStyles={styles.marginBottom25}
          />
          <FieldTextareaAutosize
            fieldId="rightsPermitOther"
            value={values.rightsPermitOther}
            label={
              <div>
                <span>אישור זכויות אחר</span>
                <ButtonTextSuggestions
                  fieldName={'rightsPermitOther'}
                  currentFieldValue={values.rightsPermitOther}
                  textSuggestionsArray={rightsPermitOther}
                  setFieldValue={setFieldValue}
                />
              </div>
            }
            containerCustomStyles={styles.marginBottom25}
          />
          <FieldTextareaAutosize
            fieldId="arnonaVoucher"
            value={values.arnonaVoucher}
            label={'שובר ארנונה'}
            containerCustomStyles={styles.marginBottom25}
          />
          <FieldTextareaAutosize
            fieldId="rightsChain"
            value={values.rightsChain}
            label={'שרשור זכויות'}
            containerCustomStyles={styles.marginBottom25}
          />
          <FieldTextareaAutosize
            fieldId="companyDocument"
            value={values.companyDocument}
            label={'מסמך מטעם החברה'}
            containerCustomStyles={styles.marginBottom25}
          />
          <FieldTextareaAutosize
            fieldId="stopConditionsAppendix"
            value={values.stopConditionsAppendix}
            label={'נספח תנאים מתלים ומפסיקים'}
            containerCustomStyles={styles.marginBottom25}
          />
        </div>
        <div className={classnames(styles.unit, styles.noBorder, styles.marginBottom25)}>
          <h3 className={classnames(styles.subHeader, styles.marginBottom25)}>מסמכים</h3>
          <div className={classnames(styles.flexBetween, styles.marginBottom25)}>
            <div>
              <FieldCheckbox
                fieldId="tama"
                label='הסכם תמ"א 38/פינוי בינוי'
                value={values.tama}
                customLabelStyles={styles.customCheckboxLabelStyles}
              />
            </div>
            <div>
              <FieldCheckbox
                fieldId="purchase"
                label="הסכם רכישה"
                value={values.purchase}
                customLabelStyles={styles.customCheckboxLabelStyles}
              />
            </div>
            <div>
              <FieldCheckbox
                fieldId="rent"
                label="הסכם שכירות"
                value={values.rent}
                customLabelStyles={styles.customCheckboxLabelStyles}
              />
            </div>
            <div>
              <FieldCheckbox
                fieldId="report"
                label="דוח מהנדס לליקויי בנייה"
                value={values.report}
                customLabelStyles={styles.customCheckboxLabelStyles}
              />
            </div>
          </div>
          {values.tama &&
            <div className={styles.marginBottom25}>
              <h4 className={classnames(styles.tabHeader, styles.marginBottom25)}>הסכם תמ"א 38/פינוי בינוי</h4>
              <div className={classnames(styles.marginBottom25)}>
                <FieldDatepicker
                  fieldId="tamaDate"
                  label={'תאריך'}
                  containerStyles={styles.halfField}
                />
              </div>
              <FieldCheckbox fieldId={'fileType27Switch'} />
              <div className={styles.attachFileContainer}>
                <ModalSharedFilesList file={tama} shumaID={shumaID} stepID={stepID}/>
                <div className={styles.divideLine}/>
                <div className={styles.attachFile}>
                  {fileUploaded.tamaFileUploaded && <MarkFileUploaded/>}
                  {!values.fileType27Switch
                    ? (
                      <FieldPasteFile
                        fieldId={'tamaFileUploaded'}
                        fileUploaded={fileUploaded.tamaFileUploaded}
                        onFileSelected={onFileSelected(fileTypeIds.tama)()}
                        setFieldValue={setFieldValue}
                      />
                    )
                    : (
                      <FieldPasteImage
                        fieldId={'tamaFileUploaded'}
                        fileUploaded={fileUploaded.tamaFileUploaded}
                        onFileSelected={onFileSelected(fileTypeIds.tama)()}
                        setFieldValue={setFieldValue}
                        formFields={'onlyFreeText'}
                      />
                    )
                  }
                </div>
              </div>
              <ListUploadedFilesViewDelete
                data={filesListData}
                fileTypeID={fileTypeIds.tama}
                shumaId={shumaID}
              />
              <FieldTextareaAutosize
                fieldId="tamaSides"
                value={values.tamaSides}
                label={'הצדדים'}
                containerCustomStyles={styles.marginBottom25}
              />
              <FieldTextareaAutosize
                fieldId="tamaLand"
                value={values.tamaLand}
                label={'המקרקעין'}
                containerCustomStyles={styles.marginBottom25}
              />
              <FieldTextareaAutosize
                fieldId="tamaEssence"
                value={values.tamaEssence}
                label={'המהות'}
                containerCustomStyles={styles.marginBottom25}
              />
            </div>
          }
          {values.purchase &&
            <div className={styles.marginBottom25}>
              <h4 className={classnames(styles.tabHeader, styles.marginBottom25)}>הסכם רכישה</h4>
              <div className={classnames(styles.marginBottom25)}>
                <FieldDatepicker
                  fieldId="purchaseDate"
                  mandatoryMark={values.purchaseSigned}
                  label={'תאריך'}
                  containerStyles={styles.halfField}
                />
              </div>
              <FieldCheckbox fieldId={'fileType28Switch'} />
              <div className={styles.attachFileContainer}>
                <ModalSharedFilesList file={purchase} shumaID={shumaID} stepID={stepID}/>
                <div className={styles.divideLine}/>
                <div className={styles.attachFile}>
                  {fileUploaded.purchaseFileUploaded && <MarkFileUploaded/>}
                  {!values.fileType28Switch
                    ? (
                      <FieldPasteFile
                        fieldId={'purchaseFileUploaded'}
                        fileUploaded={fileUploaded.purchaseFileUploaded}
                        onFileSelected={onFileSelected(fileTypeIds.purchase)()}
                        setFieldValue={setFieldValue}
                      />
                    )
                    : (
                      <FieldPasteImage
                        fieldId={'purchaseFileUploaded'}
                        fileUploaded={fileUploaded.purchaseFileUploaded}
                        onFileSelected={onFileSelected(fileTypeIds.purchase)()}
                        setFieldValue={setFieldValue}
                        formFields={'onlyFreeText'}
                      />
                    )
                  }
                </div>
              </div>
              <ListUploadedFilesViewDelete
                data={filesListData}
                fileTypeID={fileTypeIds.purchase}
                shumaId={shumaID}
              />
              <FieldCheckbox
                fieldId="purchaseSigned"
                label="הסכם חתום"
                value={values.purchaseSigned}
                customLabelStyles={styles.customCheckboxLabelStyles}
                containerStyles={styles.marginBottom25}
              />
              <FieldTextareaAutosize
                fieldId="purchaseSides"
                value={values.purchaseSides}
                label={'הצדדים'}
                containerCustomStyles={styles.marginBottom25}
              />
              <FieldTextareaAutosize
                fieldId="purchaseSelling"
                value={values.purchaseSelling}
                label={'הממכר'}
                containerCustomStyles={styles.marginBottom25}
              />
              <FieldTextareaAutosize
                fieldId="purchaseCompensation"
                value={values.purchaseCompensation}
                label={'התמורה'}
                containerCustomStyles={styles.marginBottom25}
              />
              <FieldDatepicker
                fieldId="purchaseDeliveryDate"
                label={'מועד המסירה'}
                maxDate={dayjs().add(5, 'year')}
                containerStyles={classnames(styles.marginBottom25, styles.halfField)}
              />
              <FieldTextareaAutosize
                fieldId="comments"
                value={values.comments}
                label={'הערות'}
                containerCustomStyles={styles.marginBottom25}
              />
            </div>
          }
          {values.rent &&
            <div className={styles.marginBottom25}>
              <h4 className={classnames(styles.tabHeader, styles.marginBottom25)}>הסכם שכירות</h4>
              <div className={classnames(styles.marginBottom25)}>
                <FieldDatepicker
                  fieldId="rentDate"
                  label={'תאריך'}
                  containerStyles={styles.halfField}
                />
              </div>
              <FieldCheckbox fieldId={'fileType29Switch'} />
              <div className={styles.attachFileContainer}>
                <ModalSharedFilesList file={rent} shumaID={shumaID} stepID={stepID}/>
                <div className={styles.divideLine}/>
                <div className={styles.attachFile}>
                  {fileUploaded.rentFileUploaded && <MarkFileUploaded/>}
                  {!values.fileType29Switch
                    ? (
                      <FieldPasteFile
                        fieldId={'rentFileUploaded'}
                        fileUploaded={fileUploaded.rentFileUploaded}
                        onFileSelected={onFileSelected(fileTypeIds.rent)()}
                        setFieldValue={setFieldValue}
                      />
                    )
                    : (
                      <FieldPasteImage
                        fieldId={'rentFileUploaded'}
                        fileUploaded={fileUploaded.rentFileUploaded}
                        onFileSelected={onFileSelected(fileTypeIds.rent)()}
                        setFieldValue={setFieldValue}
                        formFields={'onlyFreeText'}
                      />
                    )
                  }
                </div>
              </div>
              <ListUploadedFilesViewDelete
                data={filesListData}
                fileTypeID={fileTypeIds.rent}
                shumaId={shumaID}
              />
              <FieldSelect
                fieldId="rentEssense"
                placeholder="בחר אפשרות"
                mandatoryMark={true}
                label={'מהות השכירות'}
                options={options.rentEssenseOptions}
                containerStyles={classnames(styles.marginBottom25, styles.halfField)}
              />
              <FieldTextareaAutosize
                fieldId="rentSides"
                value={values.rentSides}
                label={'הצדדים'}
                containerCustomStyles={styles.marginBottom25}
              />
              <FieldTextareaAutosize
                fieldId="rentRentee"
                value={values.rentRentee}
                label={'המושכר'}
                containerCustomStyles={styles.marginBottom25}
              />
              <FieldTextareaAutosize
                fieldId="rentPurpose"
                value={values.rentPurpose}
                label={'מטרת השכירות'}
                containerCustomStyles={styles.marginBottom25}
              />
              <FieldTextareaAutosize
                fieldId="rentPeriod"
                value={values.rentPeriod}
                label={'תקופת השכירות'}
                containerCustomStyles={styles.marginBottom25}
              />
              <FieldTextareaAutosize
                fieldId="rentExtendOptions"
                value={values.rentExtendOptions}
                label={'אופציות להארכה'}
                containerCustomStyles={styles.marginBottom25}
              />
              <FieldInput
                fieldId="rentAnnualFeeAmount"
                value={divideThousandsIntegerValue(values.rentAnnualFeeAmount)}
                label={'דמ"ש חודשיים'}
                onChange={formatStringToNumber}
                containerCustomStyles={classnames(styles.marginBottom25, styles.halfField)}
              />
              <FieldTextareaAutosize
                fieldId="rentCertificates"
                value={values.rentCertificates}
                label={
                  <div className={styles.flex}>
                    <span className={styles.marginLeftHalf}>הערות</span>
                    <MultipleTooltip
                      id={'rentCertificatesTooltip'}
                      label={'לדוגמא : "הנכס משמש כקלינקה".'}
                    />
                  </div>
                }
                containerCustomStyles={styles.marginBottom25}
              />
              <FieldTextareaAutosize
                fieldId="rentSpecialConditions"
                value={values.rentSpecialConditions}
                label={'תנאים מיוחדים'}
                containerCustomStyles={styles.marginBottom25}
              />
            </div>
          }
          {values.report &&
            <div className={styles.marginBottom25}>
              <h4 className={classnames(styles.tabHeader, styles.marginBottom25)}>דוח מהנדס לליקויי בנייה</h4>
              <div className={classnames(styles.marginBottom25)}>
                <FieldDatepicker
                  fieldId="reportDate"
                  label={'תאריך'}
                  containerStyles={styles.halfField}
                />
              </div>
              <FieldCheckbox fieldId={'fileType30Switch'} />
              <div className={styles.attachFileContainer}>
                <ModalSharedFilesList file={report} shumaID={shumaID} stepID={stepID}/>
                <div className={styles.divideLine}/>
                <div className={styles.attachFile}>
                  {fileUploaded.reportFileUploaded && <MarkFileUploaded/>}
                  {!values.fileType30Switch
                    ? (
                      <FieldPasteFile
                        fieldId={'reportFileUploaded'}
                        fileUploaded={fileUploaded.reportFileUploaded}
                        onFileSelected={onFileSelected(fileTypeIds.report)()}
                        setFieldValue={setFieldValue}
                      />
                    )
                    : (
                      <FieldPasteImage
                        fieldId={'reportFileUploaded'}
                        fileUploaded={fileUploaded.reportFileUploaded}
                        onFileSelected={onFileSelected(fileTypeIds.report)()}
                        setFieldValue={setFieldValue}
                        formFields={'onlyFreeText'}
                      />
                    )
                  }
                </div>
              </div>
              <ListUploadedFilesViewDelete
                data={filesListData}
                fileTypeID={fileTypeIds.report}
                shumaId={shumaID}
              />
              <FieldTextareaAutosize
                fieldId="reportText"
                value={values.reportText}
                label={'פירוט'}
              />
            </div>
          }
          <FieldTextareaAutosize
            fieldId="propertyLegalDefinition"
            value={values.propertyLegalDefinition}
            label={(
              <div className={styles.flex}>
                <span className={styles.marginLeftHalf}>הגדרה משפטית של הנכס</span>
                <MultipleTooltip
                  id={'propertyLegalDefinitionTooltip'}
                  label={propertyLegalDefinitionTooltip}
                  customTooltipClassName={styles.propertyLegalDefinitionTooltip}
                />
              </div>
            )}
            containerCustomStyles={styles.marginBottom25}
          />
          <FieldTextareaAutosize
            fieldId="propertyRightsDetails"
            value={values.propertyRightsDetails}
            label={
              <div>
                <span>סיכום המצב המשפטי</span>
                <ButtonTextSuggestions
                  fieldName={'propertyRightsDetails'}
                  currentFieldValue={values.propertyRightsDetails}
                  textSuggestionsArray={propertyRightsDetails}
                  setFieldValue={setFieldValue}
                />
              </div>
            }
            containerCustomStyles={styles.marginBottom25}
          />
        </div>
        <div className={classnames(styles.refInfo)}>
          <span>  (1)</span> גבולות השטח ומימדיו
          יקבעו ע"י מודד מוסמך (לא בתחום
          סמכותו של שמאי מקרקעין)
        </div>
        <div className={styles.buttons}>
          <ButtonsSubmitCancel
            prevPagePath={prevPagePath}
            nextPagePath={nextPagePath}
          />
        </div>
      </Form>
      <UploadingFilePopUp
        showUploadingFilePopUp={showUploadingFilePopUp}
        progress={progress}
        message="הקובץ נטען, אנא המתינו"
      />
    </div>
  );
};

export default Form_1View;